import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { HardHat, ArrowsLeftRight, CaretRight } from '@phosphor-icons/react';

const STATUS_STYLES = {
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {status || 'pending'}
  </span>
);

export const SubcontractingHub = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('my-subs');
  const [subs, setSubs] = useState([]);
  const [subbing, setSubbing] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [subsRes, appsRes] = await Promise.all([
          axiosInstance.get('/applications/my-subs'),
          axiosInstance.get('/applications/my-applications'),
        ]);
        setSubs(subsRes.data.subs || []);
        setSubbing(appsRes.data.applications || []);
      } catch {} finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  if (!loaded || (subs.length === 0 && subbing.length === 0)) return null;

  const rows = tab === 'my-subs' ? subs : subbing;

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-2xl p-5 shadow-[4px_4px_0px_#000] mb-6" data-testid="subcontracting-hub">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-black text-lg uppercase tracking-wide text-neutral-900 dark:text-white flex items-center gap-2">
          <ArrowsLeftRight size={20} weight="bold" className="text-[#EA580C]" />
          Subcontracting
        </h2>
        <div className="inline-flex rounded-lg overflow-hidden border-2 border-black dark:border-neutral-600">
          <button
            onClick={() => setTab('my-subs')}
            className={`px-4 py-1.5 text-xs font-bold uppercase ${tab === 'my-subs' ? 'bg-[#EA580C] text-white' : 'bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200'}`}
            data-testid="tab-my-subs"
          >
            My Subs ({subs.length})
          </button>
          <button
            onClick={() => setTab('subbing')}
            className={`px-4 py-1.5 text-xs font-bold uppercase ${tab === 'subbing' ? 'bg-[#EA580C] text-white' : 'bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200'}`}
            data-testid="tab-subbing"
          >
            Jobs I'm Subbing On ({subbing.length})
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-neutral-500 py-4 text-center" data-testid="hub-empty">
          {tab === 'my-subs'
            ? 'No subcontractors yet — post a job open to subcontractors to build your crew.'
            : "You haven't applied to any jobs as a subcontractor yet."}
        </p>
      ) : (
        <div className="space-y-2" data-testid={`hub-list-${tab}`}>
          {rows.slice(0, 6).map((r) => (
            <button
              key={r.application_id || r._id}
              onClick={() => navigate(`/job/${r.job_id}`)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl text-left hover:border-[#EA580C] transition-colors"
              data-testid={`hub-row-${r.application_id || r._id}`}
            >
              <div className="min-w-0">
                <p className="font-bold text-sm text-neutral-900 dark:text-white truncate flex items-center gap-2">
                  <HardHat size={15} weight="bold" className="text-[#EA580C] shrink-0" />
                  {tab === 'my-subs' ? (r.sub_company || r.sub_name) : r.job_title}
                </p>
                <p className="text-xs text-neutral-500 truncate">
                  {tab === 'my-subs' ? `on "${r.job_title}"` : (r.job_location || r.job_company || '')}
                  {r.proposed_rate ? ` · ${r.proposed_rate}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={r.status} />
                <CaretRight size={14} className="text-neutral-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubcontractingHub;
